import type { BlockTxLogTimestamp } from '@/api/spec';

/**
 * Generates a unique, sortable, and persistent 64-bit ID as a BigInt from
 * block, transaction, and log indices.
 *
 * The ID is composed of:
 * - Block number (b): 40 bits (Supports up to 1 trillion+ blocks)
 * - Transaction index (tx): 12 bits (Supports up to 4,096 txs per block)
 * - Log index (l): 12 bits (Supports up to 4,096 logs per tx)
 *
 * This structure is highly scalable and ensures unique, chronological IDs
 * that are safe for use as `uint64`.
 */
export function generateBlockLogId(at: BlockTxLogTimestamp): bigint {
  const blockNumber = BigInt(at.b ?? 0);
  const txIndex = BigInt(at.tx ?? 0);
  const logIndex = BigInt(at.l ?? 0);

  // Masks and shifts for a 64-bit layout
  const blockPart = (blockNumber & 0xffffffffffn) << 24n; // Mask for 40 bits, shift by 12+12
  const txPart = (txIndex & 0xfffn) << 12n; // Mask for 12 bits, shift by 12
  const logPart = logIndex & 0xfffn; // Mask for 12 bits

  return blockPart | txPart | logPart;
}
